import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { HR_WRITE, ATT_STATUS, fmtTime, fmtDate, fmtDur } from "@/lib/constants";
import { StatusBadge } from "@/components/StatusBadge";
import { LateSummaryCard } from "@/components/attendance/LateSummaryCard";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

export default function AttendanceAdmin({ role }) {
  const [rows, setRows] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [status, setStatus] = useState("ALL");
  const [edit, setEdit] = useState(null);
  const canCorrect = HR_WRITE.includes(role);

  const load = useCallback(() => {
    const p = {};
    if (date) p.date_filter = date;
    if (status !== "ALL") p.status = status;
    api.get("/attendance", { params: p }).then((r) => setRows(r.data)).catch(() => {});
  }, [date, status]);
  useEffect(() => { load(); }, [load]);

  const saveCorrection = async () => {
    try {
      await api.put(`/attendance/${edit.id}`, {
        attendance_status: edit.attendance_status,
        notes: edit.notes,
      });
      toast.success("Koreksi absensi tersimpan (dengan audit trail)");
      setEdit(null);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-6">
      {canCorrect && <LateSummaryCard />}
      <Card className="rounded-2xl border-slate-200 dark:border-slate-800">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-6 py-4 dark:border-slate-800">
        <h3 className="font-heading text-lg font-semibold tracking-tight">
          {role === "SUPERVISOR" ? "Absensi Tim" : "Absensi Seluruh Karyawan"}
        </h3>
        <div className="flex flex-wrap items-center gap-2">
          <Input data-testid="attendance-date-filter" type="date" value={date}
            onChange={(e) => setDate(e.target.value)} className="w-40" />
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="attendance-status-filter" className="w-40">
              <SelectValue placeholder="Semua Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Semua Status</SelectItem>
              {Object.entries(ATT_STATUS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Karyawan</TableHead>
              <TableHead>Departemen</TableHead>
              <TableHead>Masuk</TableHead>
              <TableHead>Keluar</TableHead>
              <TableHead>Durasi</TableHead>
              <TableHead>Lokasi</TableHead>
              <TableHead>Selfie</TableHead>
              <TableHead>Status</TableHead>
              {canCorrect && <TableHead>Aksi</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={canCorrect ? 9 : 8} className="py-8 text-center text-slate-400">Tidak ada data untuk filter ini.</TableCell></TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.id} data-testid="admin-attendance-row">
                <TableCell>
                  <p className="font-medium">{r.full_name}</p>
                  <p className="font-mono text-xs text-slate-400">{r.employee_code}</p>
                </TableCell>
                <TableCell className="text-sm">{r.department_name}</TableCell>
                <TableCell className="font-mono text-sm">{fmtTime(r.clock_in_time)}</TableCell>
                <TableCell className="font-mono text-sm">{fmtTime(r.clock_out_time)}</TableCell>
                <TableCell className="font-mono text-sm">{fmtDur(r.work_duration)}</TableCell>
                <TableCell className="max-w-[160px] truncate text-sm" title={r.location}>{r.location}</TableCell>
                <TableCell>
                  {r.clock_in_selfie
                    ? <img src={r.clock_in_selfie} alt="selfie" className="h-9 w-9 rounded-md object-cover" data-testid="admin-selfie-thumb" />
                    : <span className="text-xs text-slate-400">-</span>}
                </TableCell>
                <TableCell>
                  <StatusBadge status={r.attendance_status} />
                  {r.corrected && <span className="ml-1 text-[10px] text-amber-500">(dikoreksi)</span>}
                </TableCell>
                {canCorrect && (
                  <TableCell>
                    <Button data-testid="correct-attendance-button" size="sm" variant="outline"
                      onClick={() => setEdit({ ...r })}>Koreksi</Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Koreksi Absensi</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-4">
              <p className="text-sm text-slate-500">{edit.full_name} · {fmtDate(edit.date)}</p>
              <div className="space-y-2">
                <Label>Status Kehadiran</Label>
                <Select value={edit.attendance_status} onValueChange={(v) => setEdit({ ...edit, attendance_status: v })}>
                  <SelectTrigger data-testid="correction-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(ATT_STATUS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Catatan Koreksi</Label>
                <Input data-testid="correction-notes-input" value={edit.notes || ""}
                  onChange={(e) => setEdit({ ...edit, notes: e.target.value })} placeholder="Alasan koreksi" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>Batal</Button>
            <Button data-testid="save-correction-button" onClick={saveCorrection}>Simpan Koreksi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
    </div>
  );
}
